/**
 * COMMIT THE QUANTITY BEFORE LEAVING THE PAGE — 9 Sep 2026
 *
 * THE BUG. The pack builder writes to the cart as you select; the quantity box
 * above it does not. So a shopper could set Quantity to 2 on a Set page, not
 * press Add to cart, scroll down, press "View your pack" — which is an ordinary
 * link to /cart — and check out with the pack and none of the Sets. The number
 * she set was never a request for anything; it was a form field, and the link
 * took her past it.
 *
 * WHAT THIS DOES. The two exits that navigate — the pack builder's footer CTA
 * and the sticky bar — commit whatever the box is holding first, then go. It is
 * the smallest change that closes the bug.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO, and why. The client asked for the box to
 * write to the cart the moment he presses +. That was designed and adversarially
 * reviewed, and it charges people who were not buying: a shopper who taps + once
 * to see what two would cost, then switches apps within the debounce, is charged
 * for two. The reviews also found a colour swap mid-write releasing the
 * single-flight lock, an unbounded retry, and a state that re-adds an item she
 * had just removed in the drawer. Money moving without a purchase gesture is a
 * much worse fault than the one being fixed, so nothing here writes while she is
 * still reading the page.
 *
 * THE RULE THAT KEEPS IT SAFE: every write is ABSOLUTE. It reconciles the line
 * to N against a cart read taken in the same breath, so asking twice is the same
 * as asking once. A retry cannot double-charge, because "make it 2" is still 2.
 * The single relative call, /cart/add.js, exists only for the 0 -> N step and is
 * never retried on its own.
 */
(function () {
  'use strict';

  var BOX = '.QuantitySelector__CurrentQuantity';
  var busy = false;

  function form() { return document.querySelector('form[action*="/cart/add"]'); }
  function box() { var f = form(); return f ? f.querySelector('[name="quantity"]') : null; }
  function variantId() {
    var f = form();
    var i = f && f.querySelector('[name="id"]');
    return i ? String(i.value) : null;
  }
  function wanted() {
    var b = box();
    if (!b) return null;
    var n = parseInt(b.value, 10);
    return isNaN(n) || n < 1 ? null : n;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (r.ok) return r.json().catch(function () { return {}; });
      return r.json().catch(function () { return {}; }).then(function (d) {
        var e = new Error(d && d.description ? d.description : 'That could not be added.');
        e.status = r.status;
        throw e;
      });
    });
  }

  /**
   * Has the shopper asked for something that has not been bought yet?
   *
   * The box means "how many to ADD", not "how many I have" — the theme resets it
   * to 1 after every successful add. So a box sitting at its default is not a
   * request; only a box the shopper has MOVED is. `armed` is that distinction,
   * and without it every exit on every page would quietly add one of whatever
   * was on screen.
   */
  var armed = false;

  function markMoved() { armed = true; }
  document.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'quantity') markMoved();
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'quantity') markMoved();
  });
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-action="increase-quantity"], [data-action="decrease-quantity"]')) {
      setTimeout(markMoved, 0);
    }
  });
  // The theme resets the box to 1 after its own add, and dispatches this. What
  // she asked for has been bought, so there is nothing left pending.
  document.addEventListener('product:added', function () { armed = false; });

  /**
   * Buy what is pending. Relative, because the box is a quantity to add.
   *
   * Not retried, and disarmed the moment it succeeds — a relative add is the one
   * call here that is not idempotent, so it gets exactly one attempt per press
   * and never an automatic second one.
   */
  function commitPending(id, want) {
    return post('/cart/add.js', { id: parseInt(id, 10), quantity: want })
      .then(function (r) { armed = false; return r; });
  }

  /** Say why nothing happened, at the button she pressed. */
  function complain(anchorEl, message) {
    var note = document.querySelector('[data-qty-commit-note]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-qty-commit-note', '');
      note.setAttribute('role', 'alert');
      note.className = 'QtyCommit__Note';
      note.style.cssText = 'margin:8px 0 0;font-size:13px;color:#b91c1c;';
      (anchorEl && anchorEl.parentNode ? anchorEl.parentNode : document.body).appendChild(note);
    }
    note.textContent = message;
  }
  function clearComplaint() {
    var note = document.querySelector('[data-qty-commit-note]');
    if (note && note.parentNode) note.parentNode.removeChild(note);
  }

  /**
   * Intercept an exit. Nothing is bought unless there is genuinely something
   * pending, and the exit is only taken once the cart agrees.
   */
  function guard(el, proceed) {
    if (busy) return;
    var id = variantId();
    var want = wanted();
    // Nothing pending: this is an ordinary link and must behave like one.
    if (!armed || !id || want === null) { proceed(); return; }

    busy = true;
    clearComplaint();
    var label = el && el.textContent;
    if (el && el.tagName === 'BUTTON') el.disabled = true;

    commitPending(id, want)
      .then(function () { busy = false; if (el && el.tagName === 'BUTTON') el.disabled = false; proceed(); })
      .catch(function (err) {
        busy = false;
        if (el && el.tagName === 'BUTTON') { el.disabled = false; if (label) el.textContent = label; }
        // A refusal must NOT navigate. Sending her to the cart having failed to
        // put the thing in it reproduces exactly the bug this file exists to fix.
        complain(el, err && err.status === 429
          ? 'Too many changes at once — give it a moment and try again.'
          : (err && err.message) || 'That could not be added. Try again.');
      });
  }

  // The pack builder's footer CTA and the sticky bar are the two exits that
  // navigate. Capture phase, so this runs before the theme's own drawer handler.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var cta = e.target.closest('[data-pack-cta]');
    if (cta) {
      // A replayed click passes straight through. Checked HERE rather than in a
      // second listener: the first version used stopPropagation() in a separate
      // capture handler, which killed the replay for everything downstream —
      // including the theme's own drawer handler — so the shopper committed her
      // Sets and then went nowhere at all.
      if (cta.getAttribute('data-qty-commit-done') === '1') return;
      if (busy) { e.preventDefault(); e.stopPropagation(); return; }
      e.preventDefault();
      e.stopPropagation();
      guard(cta, function () {
        // Replay the original click once the cart is right, letting the theme's
        // own drawer handler take it from here. The flag stops this re-entering.
        cta.setAttribute('data-qty-commit-done', '1');
        cta.click();
        cta.removeAttribute('data-qty-commit-done');
      });
      return;
    }

    var sticky = e.target.closest('.BundleStickyBar__Btn');
    if (sticky && sticky.dataset && sticky.dataset.packMode === '1') {
      e.preventDefault();
      e.stopPropagation();
      guard(sticky, function () { window.location.href = '/cart'; });
    }
  }, true);

})();
